import ListSourceWidget from "../ListSourceWidget/ListSourceWidget.js";
import TableStrategy from "../ListSourceWidget/strategies/TableStrategy.js";
import withDragAndDrop from "../ListSourceWidget/strategies/withDragAndDrop.js";

/**
 * Return a {@link ListSourceWidget} rendering a table.
 *
 * @param {ListSource} source - source of truth when it comes to
 *   fetching or manipulating the items
 * @param {GridColumnSpecification[]} columnSpecifications - Specifications
 *  for the columns of the table
 * @param {Renderable} [trailingWidget] - Optional trailing widget to
 *   append at the end of the list of items
 * @param {string} [cssClass] - Additional CSS class to append to the
 *   root of the widget
 * @param {bool} [striped] - Set to true if odd rows should have a
 *   different background
 * @param {bool} [highlightOnHover] - Set to true if the hovered row
 *   should have a different background
 * @param {bool} [bordered] - Set to true if lines should separate
 *   rows and columns
 * @param {object} [dragAndDrop] - Options related to the drag and drop feature
 * @param {function} [dragAndDrop.isDraggable] - Optional predicate invoked with an item and its index in the list of items.
 *   When this function return `true`, support for drag is added to this item
 * @param {function} [dragAndDrop.isDroppable] - Optional predicate invoked with an item and its index in the list of items.
 *   When this function return `true`, support for drop is added to this item
 * @param {function} [dragAndDrop.onManualSort] - Optional callback to invoke when sortable items are sorted.
 *   This function is invoked with the list of sortable items, in the order chosen by the user
 * @param {function(DOMElement):DOMElement} [dragAndDrop.getDragHandle] - When
 *   provided, narrow down the drag handle to the result of the function. This
 *   function is invoked with the `tr` DOMElement
 * @param {string} messageForEmptyContent - Message that will be shown when there is no data to list
 * @param {Renderer} [tfootRenderer] - If defined, it must be a
 *   function accepting an object with `items` as key. The function
 *   should return a Renderable. The Renderable will be added to a
 *   <tfoot> DOM node
 */
export default function makeTableListWidget({
	source,
	columnSpecifications,
	trailingWidget,
	cssClass,
	striped,
	highlightOnHover,
	bordered,
	tfootRenderer,
	dragAndDrop: {
		isDraggable,
		isDroppable,
		onManualSort = () => {},
		getDragHandle,
	} = {},
	messageForEmptyContent,
}) {
	let strategy = new TableStrategy({
		columnSpecifications,
		trailingWidget,
		cssClass,
		striped,
		highlightOnHover,
		bordered,
		messageForEmptyContent,
		tfootRenderer,
	});

	if (isDraggable ?? isDroppable) {
		isDraggable ??= () => false;
		isDroppable ??= () => false;

		strategy = withDragAndDrop(strategy, {
			isDraggable,
			isDroppable,
			onChange: onManualSort,
			getDragHandle,
		});
	}

	return new ListSourceWidget({ strategy, source });
}
